import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import MyIncentivesTable from "./MyIncentivesTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import DialogCell from "../Spiffs/Tables/components/Cells/DialogCell";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Helpers
import { getMyMotivators } from "./MyIncentivesFactory";
import { getSpiffDepartment } from "@Motivators/helpers/spiffs/position";
import { getFormatDate } from "@Common/helpers/Dates";
// MUI
import { Grid, Stack, Typography } from "@mui/material";

// Helpers
import { accountFormat } from "@/Common/helpers/number";
import { getDepartmentalPosition } from "@/Common/helpers/Positions";

// Assets
import empty from "@/assets/images/commons/empty.png";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Constants
import POSITIONS from "@/Constants/positions";
import {
	COLUMN_RESIZE_FACTOR,
	ONE_HUNDRED,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_FORTY,
	ONE_HUNDRED_SIXTY,
	ONE_HUNDRED_TWENTY,
	TWO_HUNDRED,
	TWO_HUNDRED_TWENTY,
} from "@/Constants/tables";
import { and } from "@/Common/helpers/sonar";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

function formatDayLabel(value) {
	return value > 1 ? `${value} days` : `${value} day`;
}

function formatHours(value) {
	const hours = Math.floor(value);
	const minutes = Math.round((value - hours) * 60);
	return `${hours}h ${minutes}m`;
}

function cellRenderer(params) {
	return (
		<TypographyCell fontSize="14.5px" tooltipProps={{ title: params.formattedValue }}>
			{params.formattedValue}
		</TypographyCell>
	);
}

const tableNoWrapLabel = "table-noWrap";

export default function MyIncentivesTableList(props) {
	const location = useSelector((state) => state.location.location);
	const loggedUser = useSelector((state) => state.user.user);
	const locationId = location?.locationId;
	const employeeId = loggedUser?.employeeId;
	const currentUserId = loggedUser?._id;
	const queryClient = useQueryClient();

	const department = getSpiffDepartment(loggedUser?.position);
	const { incentives, queries, subscriptions } = getMyMotivators(department);

	// Subscription
	const { getSubscription } = useSubscriptions();
	const myIncentivesSubscription = getSubscription(["myIncentives", props.selected?._id]);
	myIncentivesSubscription?.setInvalidateList([
		["myIncentivesFirstMotivator", locationId, props.selected?._id, currentUserId],
		["myIncentivesSecondMotivator", locationId, employeeId, props.selected?._id],
		["myIncentivesThirdMotivator", locationId, props.selected?._id, currentUserId],
	]);

	const options = {
		spiffs: {
			color: "var(--blue-background) !important",
			column: [
				{
					field: "id",
					headerName: "ID",
					width: 0,
					hide: true,
				},
				{
					field: "title",
					headerName: "Name",
					minWidth: ONE_HUNDRED_FORTY,
					width: ONE_HUNDRED_FORTY,
					maxWidth: ONE_HUNDRED_FORTY * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					renderCell: ({ formattedValue }) => <CustomCell value={formattedValue} hasTooltip />,
				},
				{
					field: "spiffType",
					headerName: "Spiff Type",
					minWidth: ONE_HUNDRED_TWENTY,
					width: ONE_HUNDRED_TWENTY,
					maxWidth: ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					renderCell: (params) => <CustomCell value={params.value} />,
				},
				{
					field: "eligibility",
					headerName: "Eligibility Requirements",
					minWidth: TWO_HUNDRED,
					width: TWO_HUNDRED,
					maxWidth: TWO_HUNDRED * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					renderCell: (params) => {
						const { title, spiffType } = params.row;
						const { value } = params;

						if (!spiffType || spiffType === "Generic") {
							return <CustomCell value={value.genericValue} />;
						}

						return <DialogCell requirements={value} editModeActive={false} title={title} spiffType={spiffType} />;
					},
				},
				{
					field: "createdAt",
					headerName: "Date Awarded",
					resizable: false,
					minWidth: ONE_HUNDRED_SIXTY,
					width: ONE_HUNDRED_SIXTY,
					maxWidth: ONE_HUNDRED_SIXTY * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "requiredDealStatus",
					headerName: "Minimum Required Deal Status",
					resizable: false,
					minWidth: ONE_HUNDRED_FIFTY,
					width: ONE_HUNDRED_FIFTY,
					maxWidth: ONE_HUNDRED_FIFTY * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					renderCell: (params) => <CustomCell value={params.value} />,
				},
				{
					field: "cars",
					headerName: department === "Service" ? "Repair Orders" : "Stocks",
					minWidth: TWO_HUNDRED_TWENTY,
					width: TWO_HUNDRED_TWENTY,
					maxWidth: TWO_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					renderCell: (params) => {
						const stocks = Array.isArray(params.value) ? params.value.join(", ") : params.value;
						return <CustomCell value={stocks} hasTooltip maxWith={params.colDef.width} />;
					},
				},
				{
					field: "reward",
					headerName: "Reward",
					width: ONE_HUNDRED,
					align: "right",
					resizable: false,
					type: "number",
					cellClassName: "no-focus cell-with-dollar-symbol",
					renderHeader: (params) => <TypographyHeader params={params} right={false} className="text-sx font-medium" />,
					valueFormatter: (value) => accountFormat(value),
				},
			],
			title: "Spiffs",
		},
		specialUnits: {
			color: "var(--medium-pink-color) !important",
			column: [
				{
					field: "id",
					headerName: "ID",
					width: 0,
					hide: true,
				},
				{
					field: "saleDate",
					headerName: "Sale Date",
					width: 130,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "dealNumber",
					headerName: "Deal Number",
					width: 125,
				},
				{
					field: "stockNumber",
					headerName: "Stock",
					width: 80,
				},
				{
					field: "customerName",
					headerName: "Customer Name",
					width: 250,
				},
				{
					field: "description",
					headerName: "Description",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "commission",
					headerName: "Commission",
					width: 130,
					valueFormatter: (value) => `$ ${value.toFixed(2)}`,
				},
			],
			title: "Special Units",
		},
		autoAlerts: {
			color: "var(--strong-purple-color) !important",
			column: [
				{
					field: "_id",
					headerName: "ID",
					hide: true,
				},
				{
					field: "date",
					headerName: "Sale Date",
					flex: 1,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "lastActivityDate",
					headerName: "Last Activity Date",
					flex: 1,
					width: 160,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "saleType",
					headerName: "Activity Type",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "actionType",
					headerName: "Action Type",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "initiatedBy",
					headerName: "Initiated By",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					valueFormatter: (value) => String(value),
					renderCell: cellRenderer,
				},

				{
					field: "customer",
					headerName: "Customer",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "associationWindow",
					headerName: "Assoc. Window",
					flex: 1,
					width: 150,
					renderCell: (row) => formatDayLabel(row.value),
				},
				{
					field: "amount",
					headerName: "Comm.",
					flex: 1,
					cellClassName: tableNoWrapLabel,
					renderCell: (row) => {
						return (
							<Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
								<Grid item>
									<Typography>$</Typography>
								</Grid>
								<Grid item alignSelf={"right"}>
									<Typography className="regular-pt-sans-pro-narrow-label" component="p">
										{row.formattedValue}
									</Typography>
								</Grid>
							</Stack>
						);
					},
				},
			],
			title: "Opportunity Mining",
		},
		spiffsBonuses: {
			color: "var(--blue-background) !important",
			column: [
				{
					field: "id",
					headerName: "ID",
					width: 0,
					hide: true,
				},
				{
					field: "title",
					headerName: "Name",
					width: 150,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "eligibility",
					headerName: "Eligibility Requirements",
					minWidth: 300,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "spiffDate",
					headerName: "Date Awarded",
					width: 200,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "type",
					headerName: "Type",
					width: 100,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "description",
					headerName: "Description",
					width: 150,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "reward",
					headerName: "Reward",
					width: 180,
					valueFormatter: (value) => `$ ${value?.toFixed(2)}`,
				},
			],
			title: "Spiffs/Bonuses",
		},
		training: {
			color: "var(--medium-pink-color) !important",
			column: [
				{
					field: "id",
					headerName: "ID",
					width: 0,
					hide: true,
				},
				{
					field: "createdAt",
					headerName: "Entry Date",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "createdBy",
					headerName: "Added By",
					width: 150,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "effectiveDateFrom",
					headerName: "Effective Date From",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "effectiveDateTo",
					headerName: "Effective Date To",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "pay",
					headerName: "Pay",
					width: 180,
					valueFormatter: (value) => {
						switch (value.input) {
							case "Duration":
								return formatHours(value.value);
							case "Dollar":
							default:
								return `$ ${value?.value.toFixed(2)}`;
						}
					},
				},
			],
			title: "Training",
		},
		temporaryGuaranteed: {
			color: "var(--strong-purple-color) !important",
			column: [
				{
					field: "id",
					headerName: "ID",
					width: 0,
					hide: true,
				},
				{
					field: "entryDate",
					headerName: "Entry Date",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "addedBy",
					headerName: "Added By",
					width: 150,
					cellClassName: tableNoWrapLabel,
					renderCell: cellRenderer,
				},
				{
					field: "effectiveDateFromItem",
					headerName: "Effective Date From",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "effectiveDateToItem",
					headerName: "Effective Date To",
					width: 250,
					valueFormatter: (value) => getFormatDate(value, DATE_FORMAT.monthAbbrDatePadded),
				},
				{
					field: "amount",
					headerName: "Amt. per Pay Period",
					width: 180,
					valueFormatter: (value) => `$ ${value?.toFixed(2)}`,
				},
			],
			title: "Temporary Guaranteed",
		},
	};

	const [
		{ data: firstMotivatorData, isLoading: firstMotivatorLoading },
		{ data: secondMotivatorData, isLoading: secondMotivatorLoading },
		{ data: thirdMotivatorData, isLoading: thirdMotivatorLoading },
		{ data: titleThirdMotivatorData },
	] = useQueries({
		queries: [
			{
				queryKey: ["myIncentivesFirstMotivator", locationId, props.selected?._id, currentUserId],
				queryFn: async () => {
					return queries.getFirstMotivator({
						locationId,
						department,
						recipientId: currentUserId,
						commissionCycleId: props.selected?._id,
					});
				},
				enabled: and(!!props.selected, !!locationId, !!currentUserId),
				staleTime: Infinity,
			},
			{
				queryKey: ["myIncentivesSecondMotivator", locationId, employeeId, props.selected?._id],
				queryFn: async () => {
					return queries.getSecondMotivator({
						locationId,
						employeeId,
						userId: currentUserId,
						commissionCycleId: props.selected?._id,
					});
				},
				enabled: and(!!props.selected, !!employeeId, !!locationId, !!currentUserId),
				staleTime: Infinity,
			},
			{
				queryKey: ["myIncentivesThirdMotivator", locationId, props.selected?._id, currentUserId],
				queryFn: async () => queries.getThirdMotivator(locationId, currentUserId, props.selected?._id),
				enabled: and(!!props.selected, !!locationId, !!currentUserId),
				staleTime: Infinity,
			},
			{
				queryKey: ["getProviderNameOpportunityMining", locationId],
				queryFn: async () => queries.getProviderName({ locationId, title: options[incentives.thirdMotivator].title }),
				staleTime: Infinity,
			},
		],
	});

	if (subscriptions?.length) {
		(Object.keys(subscriptions) || []).forEach((key) => {
			myIncentivesSubscription?.subscriptions?.[key]?.setCallback((res) =>
				subscriptions[key]({ res, queryClient, locationId, employeeId, currentUserId })
			);
		});
	}

	const showSpecialUnits = !(
		incentives.secondMotivator === "specialUnits" && getDepartmentalPosition(loggedUser.position) !== POSITIONS.SALES.SALES_ASSOCIATE
	);

	const IncentiveTabs = [
		`My ${options[incentives.firstMotivator].title}`,
		`My ${options[incentives.secondMotivator].title}`,
		`My ${titleThirdMotivatorData}`,
	].filter((_, tabIndex) => tabIndex !== 1 || showSpecialUnits);

	const IncentiveContent = [
		<MyIncentivesTable
			key={1}
			loading={firstMotivatorLoading}
			data={firstMotivatorData ?? []}
			incentive={incentives.firstMotivator}
			options={options}
		/>,
		<MyIncentivesTable
			key={2}
			loading={secondMotivatorLoading}
			data={secondMotivatorData ?? []}
			incentive={incentives.secondMotivator}
			options={options}
		/>,
		<MyIncentivesTable
			key={3}
			loading={thirdMotivatorLoading}
			data={thirdMotivatorData ?? []}
			incentive={incentives.thirdMotivator}
			options={options}
		/>,
	].filter((_, tabIndex) => tabIndex !== 1 || showSpecialUnits);

	return (
		<RoundedBox className="!p-0 min-h-[630px]">
			{!props.selected ? (
				<EmptyBody
					image={empty}
					title="No commissions cycle selected."
					bodyHeight="510px"
					sx={{
						boxShadow: "none !important",
					}}
				/>
			) : (
				<TabSelectable tabs={IncentiveTabs} content={IncentiveContent} tabsProps={{ style: { marginTop: "30px" } }} />
			)}
		</RoundedBox>
	);
}

MyIncentivesTableList.propTypes = {
	selected: PropTypes.shape({
		_id: PropTypes.string,
	}),
};
